import { BlockProps } from '@/components/Block/Block';
import { useBlockLocalUserData } from '@/hooks/useBlockLocalUserData';
import P from '@/components/Redactie/P/P';
import { useEffect, useRef, useState } from 'react';
import BlockEditorContainer from '@/components/Block/BlockEditorContainer';
import { Checkbox, FormControlLabel } from '@mui/material';
import { UserMessageBlockViewerContainer } from '@/components/Block/BlockViewerContainer';
import {
    mdiAlert,
    mdiChevronRightCircle,
    mdiOpenInApp,
    mdiReload,
} from '@mdi/js';
import TextButton from '@/components/Button/TextButton';
import { Dialog, DialogBackdrop } from '@headlessui/react';
import { motion, useReducedMotion } from 'framer-motion';
import Icon from '@mdi/react';

interface EmbedBlockData {
    description: string;
    url: string;
    is_fullscreen: boolean;
}

interface EmbedBlockLocalData {
    has_been_finished: boolean;
}

const EmbedBlock = ({
    subjectId,
    id,
    data,
    isEditable,
    uuid,
    updatedAt,
    isAnimated,
    isEditorPreview,
    onFinished,
    onEdited,
}: BlockProps) => {
    const [blockData, setBlockData] = useState<EmbedBlockData>({
        description: data.description ?? 'Voeg een omschrijving toe',
        url: data.url ?? 'https://ntr.nl',
        is_fullscreen: data.is_fullscreen ?? true,
    });

    if (isEditable)
        return (
            <EmbedBlockEditor
                uuid={uuid}
                blockData={blockData}
                setBlockData={setBlockData}
                onEdited={onEdited}
            />
        );
    else
        return (
            <EmbedBlockViewer
                subjectId={subjectId}
                id={id}
                blockData={blockData}
                updatedAt={updatedAt}
                isEditorPreview={isEditorPreview}
                isAnimated={isAnimated}
                onFinished={onFinished}
            />
        );
};

const EmbedBlockEditor = ({
    uuid,
    blockData,
    setBlockData,
    onEdited,
}: {
    uuid: BlockProps['uuid'];
    blockData: EmbedBlockData;
    setBlockData: (data: EmbedBlockData) => void;
    onEdited: BlockProps['onEdited'];
}) => {
    useEffect(() => {
        onEdited?.({
            uuid,
            data: JSON.parse(JSON.stringify(blockData)),
            attachments: {},
        });
    }, [blockData]);

    return (
        <BlockEditorContainer className="bg-green-200">
            <P
                isEditable
                value={blockData.description}
                onChange={(value) => {
                    setBlockData({ ...blockData, description: value });
                }}
                placeholder="Beschrijving"
                rows={1}
                maxLength={24}
            />
            <div className="ps-2">
                <FormControlLabel
                    onChange={(event) => {
                        const checked = (event.target as HTMLInputElement)
                            .checked;
                        setBlockData({
                            ...blockData,
                            is_fullscreen: checked,
                        });
                    }}
                    control={
                        <Checkbox
                            size="small"
                            checked={blockData.is_fullscreen}
                        />
                    }
                    label={
                        <span className="text-sm font-bold">
                            Volledig scherm
                        </span>
                    }
                    labelPlacement="end"
                />
            </div>
            <P
                isEditable
                value={blockData.url}
                onChange={(value) => {
                    setBlockData({ ...blockData, url: value });
                }}
                placeholder="Link"
                rows={1}
            />
            <p className="flex mx-2 gap-2 font-bold">
                <Icon path={mdiAlert} size={1} /> Gebruik alleen een link naar
                een site als deze aan de WCAG richtlijnen voldoet én geen eigen
                cookies plaatst.
            </p>
        </BlockEditorContainer>
    );
};

const EmbedBlockViewer = ({
    subjectId,
    id,
    blockData,
    updatedAt,
    isEditorPreview,
    isAnimated,
    onFinished,
}: {
    subjectId: BlockProps['subjectId'];
    id: BlockProps['id'];
    blockData: EmbedBlockData;
    updatedAt: BlockProps['updatedAt'];
    isEditorPreview: BlockProps['isEditorPreview'];
    isAnimated: BlockProps['isAnimated'];
    onFinished: BlockProps['onFinished'];
}) => {
    const reduceMotion = useReducedMotion();
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const { userDataLocal, setUserDataLocal } =
        useBlockLocalUserData<EmbedBlockLocalData>(
            subjectId,
            id,
            updatedAt,
            isEditorPreview,
            {
                has_been_finished: false,
            },
        );

    const [isOpened, setIsOpened] = useState(false);
    const [title, setTitle] = useState(blockData.description);

    // Update the title every second from the iframe source document
    useEffect(() => {
        const interval = setInterval(() => {
            if (isOpened) {
                try {
                    const newTitle =
                        iframeRef?.current?.contentDocument?.title ??
                        iframeRef?.current?.contentWindow?.document?.title;
                    if (newTitle) setTitle(newTitle);
                } catch {
                    // Ignore errors, it's not a big deal
                }
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [isOpened, setTitle]);

    return (
        <>
            <UserMessageBlockViewerContainer
                label={title}
                icon={mdiOpenInApp}
                className={
                    isOpened && !blockData.is_fullscreen ? 'w-full' : undefined
                }
                labelClassName="text-primary-700"
                isCollapsed={false}
                isAnimated={isAnimated}
            >
                {!isOpened && (
                    <TextButton
                        icon={mdiOpenInApp}
                        onClick={() => setIsOpened(true)}
                        isSmall={userDataLocal.has_been_finished}
                    >
                        Starten
                    </TextButton>
                )}

                <Dialog
                    open={isOpened && blockData.is_fullscreen}
                    onClose={() => {
                        if (confirm('Weet je zeker dat je wilt afsluiten?'))
                            setIsOpened(false);
                    }}
                    className="fixed inset-0 z-200"
                >
                    <DialogBackdrop className="fixed inset-0 bg-primary" />
                    <Dialog.Panel className="fixed inset-0 sm:p-1 md:p-2 lg:p-4">
                        <motion.div
                            className="flex flex-col w-full h-full bg-white sm:rounded-2xl overflow-hidden shadow-2xl"
                            initial={{
                                opacity: 0,
                                scale: reduceMotion ? 1 : 0.8,
                            }}
                            animate={{
                                opacity: 1,
                                scale: 1,
                            }}
                        >
                            <Dialog.Title className="flex flex-wrap items-center justify-center gap-4 p-4 text-xl font-bold max-w-4xl mx-auto w-full">
                                <span className="hidden md:block ms-2 me-auto">
                                    {title}
                                </span>
                                <TextButton
                                    icon={mdiReload}
                                    onClick={() => {
                                        if (
                                            iframeRef.current &&
                                            confirm(
                                                'Weet je zeker dat je deze app opnieuw wilt starten? Mogelijk is er dan voortgang verloren.',
                                            )
                                        )
                                            iframeRef.current.src =
                                                blockData.url;
                                    }}
                                    isSmall
                                    isPrimary={false}
                                >
                                    Herstarten
                                </TextButton>
                                <TextButton
                                    icon={mdiChevronRightCircle}
                                    onClick={() => {
                                        if (
                                            iframeRef.current &&
                                            confirm(
                                                'Weet je zeker dat je wilt afronden?',
                                            )
                                        ) {
                                            setIsOpened(false);
                                            setUserDataLocal({
                                                has_been_finished: true,
                                            });
                                            onFinished?.();
                                        }
                                    }}
                                    isSmall
                                >
                                    Afronden
                                </TextButton>
                            </Dialog.Title>
                            <iframe
                                ref={iframeRef}
                                src={blockData.url}
                                className="grow w-full h-full bg-white"
                                allowFullScreen
                                title={title}
                                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-top-navigation-by-user-activation"
                            />
                        </motion.div>
                    </Dialog.Panel>
                </Dialog>

                {isOpened && !blockData.is_fullscreen && (
                    <>
                        <motion.iframe
                            ref={iframeRef}
                            src={blockData.url}
                            className="grow w-full h-[46rem] max-h-[90vh] bg-white rounded-xl shadow-lg mb-4"
                            title={title}
                            initial={{
                                opacity: 0,
                                scale: reduceMotion ? 1 : 0.8,
                            }}
                            animate={{
                                opacity: 1,
                                scale: 1,
                            }}
                            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-top-navigation-by-user-activation"
                        />
                        <div className="flex flex-wrap items-center justify-center gap-4">
                            <TextButton
                                icon={mdiReload}
                                onClick={() => {
                                    if (
                                        iframeRef.current &&
                                        confirm(
                                            'Weet je zeker dat je deze app opnieuw wilt starten? Mogelijk is er dan voortgang verloren.',
                                        )
                                    )
                                        iframeRef.current.src = blockData.url;
                                }}
                                isSmall
                                isPrimary={false}
                            >
                                Herstarten
                            </TextButton>
                            <TextButton
                                icon={mdiChevronRightCircle}
                                onClick={() => {
                                    setIsOpened(false);
                                    setUserDataLocal({
                                        has_been_finished: true,
                                    });
                                    onFinished?.();
                                }}
                                isSmall
                            >
                                Afronden
                            </TextButton>
                        </div>
                    </>
                )}
            </UserMessageBlockViewerContainer>
        </>
    );
};

export default EmbedBlock;
